import { memo, type ReactNode, useCallback, useId, useState } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';
import { cn, tailwindVariants, type VariantProps } from '@air/utils-tailwind';

// Slots rather than three separate definitions: the variants differ in where the padding and border
// sit, which is a decision across the three elements at once rather than three separate ones.
const workspaceAgentDisclosure = tailwindVariants({
  slots: {
    body: '',
    root: '',
    // The row states its own text metrics rather than inheriting them: the trigger wrapper would
    // otherwise take a strut from the ambient font size, which makes its line box taller than the
    // summary's and lands the two baselines a pixel apart.
    trigger:
      'flex w-full items-center gap-2 text-left text-12 leading-4 focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8',
  },
  variants: {
    variant: {
      // Sits directly against a message, where chrome of its own would read as a second bubble.
      plain: {
        root: 'space-y-2',
      },
      // A form section, which has to look like something openable rather than more copy.
      card: {
        body: 'ads:border-subtle border-gray-3 border-t px-3 py-3',
        root: 'overflow-hidden rounded-8 border ads:border-default border-gray-4 ads:bg-base bg-gray-1',
        trigger: 'px-3 py-2',
      },
      /** Inline expand in chat history (prototype `CollectionChip`, uncited asset scope). */
      scope: {
        body: 'ads:border-ads-gray-5 border-gray-5 border-t p-0',
        root: 'w-full min-w-0 overflow-hidden rounded-6 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1',
        trigger: 'px-2 py-1.5',
      },
    },
  },
});

export type WorkspaceAgentDisclosureVariant = NonNullable<VariantProps<typeof workspaceAgentDisclosure>['variant']>;

export type WorkspaceAgentDisclosureProps = {
  children: ReactNode;
  'data-testid'?: string;
  /** Only consulted while uncontrolled; a controlled disclosure takes its open state from the caller. */
  defaultIsExpanded?: boolean;
  /** Supplying this makes the disclosure controlled. Withhold it to let it track its own state. */
  isExpanded?: boolean;
  /** When true, the trigger cannot expand or collapse the section. */
  isDisabled?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  /** Shown only while collapsed, so a closed section still says what it is holding. */
  summary?: ReactNode;
  trigger: ReactNode;
  /** When set, applied to the expand/collapse button for Cypress and keyboard focus targets. */
  triggerTestId?: string;
  /**
   * The trigger's text in full, for a `trigger` that truncates. Belongs here rather than around the
   * label at the call site because the button is what takes focus, and a tooltip on anything inside
   * it would open by pointer only.
   */
  triggerTooltip?: string;
  variant: WorkspaceAgentDisclosureVariant;
};

/**
 * A labelled section that opens on demand.
 *
 * Exists because this pattern appears throughout the panel — the run behind a result, each section of
 * a settings form, a table of what a write would change — and each instance otherwise rebuilds the
 * same `aria-expanded`/`aria-controls` pairing and chevron rotation, which is exactly the part that
 * gets forgotten.
 */
export const WorkspaceAgentDisclosure = memo(
  ({
    children,
    'data-testid': testId,
    defaultIsExpanded = false,
    isDisabled = false,
    isExpanded,
    onExpandedChange,
    summary,
    trigger,
    triggerTestId,
    triggerTooltip,
    variant,
  }: WorkspaceAgentDisclosureProps) => {
    const [uncontrolledIsExpanded, setUncontrolledIsExpanded] = useState(defaultIsExpanded);
    const panelId = useId();
    const styles = workspaceAgentDisclosure({ variant });

    const isControlled = isExpanded !== undefined;
    const isOpen = isControlled ? isExpanded : uncontrolledIsExpanded;
    const isSummaryVisible = Boolean(summary) && !isOpen && !isDisabled;

    const handleToggle = useCallback(() => {
      if (isDisabled) {
        return;
      }

      const nextIsExpanded = !isOpen;

      if (!isControlled) {
        setUncontrolledIsExpanded(nextIsExpanded);
      }

      onExpandedChange?.(nextIsExpanded);
    }, [isControlled, isDisabled, isOpen, onExpandedChange]);

    return (
      <div className={styles.root()} data-testid={testId}>
        {/* Renders the button bare when no tooltip is given, so an untooltipped disclosure gains
            nothing — not even a wrapper. */}
        <Tooltip label={triggerTooltip}>
          <button
            aria-controls={panelId}
            aria-expanded={isOpen && !isDisabled}
            className={cn(styles.trigger(), isDisabled && 'cursor-not-allowed opacity-60')}
            data-testid={triggerTestId}
            disabled={isDisabled}
            onClick={handleToggle}
            type="button"
          >
            {/* The trigger keeps its content width and the summary takes only what is left over, so a
                long summary truncates instead of starving the title into a second line. `flex-1` moves
                to the trigger when no summary is showing, since that is what holds the chevron against
                the right edge. */}
            <span className={cn('min-w-0', !isSummaryVisible && 'flex-1')}>{trigger}</span>
            {isSummaryVisible ? (
              <span className="min-w-0 flex-1 truncate text-right font-medium ads:text-metadata text-12 text-gray-9 leading-4">
                {summary}
              </span>
            ) : null}
            <ChevronDownIcon
              className={cn('shrink-0 ads:text-icon-subtle text-gray-9 transition-transform', isOpen && 'rotate-180')}
              size="sm"
            />
          </button>
        </Tooltip>
        {/* Unmounted rather than hidden, so a collapsed section keeps its contents out of the
            accessibility tree entirely instead of leaving them there to be read. */}
        {isOpen && !isDisabled ? (
          <div className={styles.body()} id={panelId}>
            {children}
          </div>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentDisclosure.displayName = 'WorkspaceAgentDisclosure';
